import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import { useTranslation } from 'react-i18next';
import { showMessage } from 'app/store/fuse/messageSlice';
import { useDispatch } from 'react-redux';
import { Typography } from '@mui/material';
import client from 'client';
import store from 'app/store';
import { getBrowserInfo, getGeoLocationInfo, getClientIpInfo } from 'app/main/loadz/utils';



// Country codes for phone input
const COUNTRY_CODES = [
  { code: 'us', label: 'US', dial: '+1', flag: '🇺🇸' },
  { code: 'mx', label: 'MX', dial: '+52', flag: '🇲🇽' }
];

export default function ModalAddPortalUser({ open, onClose, customerId, onUserCreated, prop_data }) {
  const { t } = useTranslation('navigation');
  const dispatch = useDispatch();

  // Portal User Roles Configuration
const PORTAL_ROLES = [
  {
    value: 'customer-portal-viewer',
    label: t('VIEWER') || 'Viewer',
    description: t('CAN_VIEW_INFORMATION_ONLY') || 'Can view information only'
  },
  {
    value: 'customer-portal-payer',
    label: t('PAYER') || 'Payer',
    description: t('CAN_VIEW_AND_MAKE_PAYMENTS') || 'Can view and make payments'
  },
  {
    value: 'customer-portal-accountant',
    label: t('ACCOUNTANT') || 'Accountant',
    description: t('CAN_VIEW_AND_MANAGE_FINANCIAL_INFORMATION') || 'Can view and manage financial information'
  },
  {
    value: 'customer-portal-admin',
    label: t('ADMIN') || 'Admin',
    description: t('ADMIN_WITH_FULL_ACCESS_TO_ALL_FEATURES') || 'Full access to customer portal'
  }
];
  
  // Form state
  const [formData, setFormData] = useState({
    customer_id: customerId,
    user_email: '',
    user_fname: '',
    user_lname: '',
    user_phone: '',
    role_name: 'customer-portal-viewer', // Default role
    country_code: 'us'
  });
  
  const [loading, setLoading] = useState(false);

  const _showMessage = (msg, color) => {
    dispatch(
      showMessage({
        message: (
          <>
            <Typography>{msg}</Typography>
          </>
        ),
        autoHideDuration: 6000,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        variant: color,
      })
    );
  };

  const resetForm = () => {
    setFormData({
      customer_id: customerId,
      user_email: '',
      user_fname: '',
      user_lname: '',
      user_phone: '',
      role_name: 'customer-portal-viewer',
      country_code: 'us'
    });
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    // Validate first name
    if (!formData.user_fname.trim()) {
      _showMessage(t('PLEASE_ENTER_FIRST_NAME') || 'Please enter a first name', 'error');
      return false;
    }

    // Validate last name
    if (!formData.user_lname.trim()) {
      _showMessage(t('PLEASE_ENTER_LAST_NAME') || 'Please enter a last name', 'error');
      return false;
    }

    // Validate email
    if (!formData.user_email.trim()) {
      _showMessage(t('PLEASE_ENTER_EMAIL') || 'Please enter an email', 'error');
      return false;
    }

    // Enhanced email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.user_email)) {
      _showMessage(t('PLEASE_ENTER_VALID_EMAIL') || 'Please enter a valid email address', 'error');
      return false;
    }

  // Validate phone
  if (!formData.user_phone.trim()) {
    _showMessage(t('PLEASE_ENTER_PHONE') || 'Please enter a phone number', 'error');
    return false;
  }

  // Validate phone format (digits only, min 10 digits)
  const phoneDigits = formData.user_phone.replace(/\D/g, '');
  if (phoneDigits.length < 10) {
    _showMessage(t('PLEASE_ENTER_VALID_PHONE') || 'Please enter a valid phone number (minimum 10 digits)', 'error');
    return false;
  }

  return true;
};

  const handleCreate = async () => {
    // Validate all fields
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Get user information from store
      const currentUser = store.getState().loadz_auth.AuthReducer.user;
      const compacctnum = prop_data?.compacctnum || currentUser.company.compacctnum;
      const currentUserId = currentUser.user_id;
      
      // Get browser information
      const browserInfo = getBrowserInfo();
      
      // Get geolocation and IP information in parallel
      const [geoInfo, ipInfo] = await Promise.all([
        getGeoLocationInfo(),
        getClientIpInfo()
      ]);
      
      // Prepare request payload following API specification
      const requestData = {
        customer_id: customerId,
        user_email: formData.user_email.trim(),
        user_fname: formData.user_fname.trim(),
        user_lname: formData.user_lname.trim(),
        user_phone: formData.user_phone,
        country_code: formData.country_code,
        role_name: formData.role_name,
        created_by_user_id: currentUserId,
        browser_info: {
          name: browserInfo.name,
          version: browserInfo.version.split('.')[0], // Major version only
          fullVersion: browserInfo.version,
          os: browserInfo.os
        },
        geo_info: geoInfo,
        ip_info: ipInfo
      };

      const result = await client.post(
        `customers/add-new-customer-portal-user/${compacctnum}`,
        requestData
      );


      if (result?.status === 200 || result?.status === 201) {
        const selectedRole = PORTAL_ROLES.find(r => r.value === formData.role_name);
        const successMsg = t('PORTAL_USER_INVITED_SUCCESS') || 
          `Portal user invited successfully as ${selectedRole?.label || 'User'}. They will receive an invitation email.`;
        
        _showMessage(successMsg, 'success');
        
        if (onUserCreated) {
          onUserCreated(result.data);
        }
        
        handleClose();
      } else {
        const errorMsg = result?.data?.message || result?.data?.error || 'Error inviting portal user';
        _showMessage(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Error inviting portal user:', error);
      
      // Enhanced error handling
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error ||
                          error?.message ||
                          'Error inviting Customer Portal User';
      
      _showMessage(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Render dialog
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
      container={() => document.body}
      disablePortal={false}
      PaperProps={{
        sx: {
          backgroundColor: 'white',
          zIndex: 9999
        }
      }}
      sx={{
        '& .MuiDialog-container': {
          pointerEvents: 'none',
          '& .MuiPaper-root': {
            pointerEvents: 'auto',
          },
        },
      }}
    >
      <DialogTitle sx={{ backgroundColor: '#edca33', fontWeight: 'bold' }}>
        {t('ADD_NEW_CUSTOMER_PORTAL_USER') || 'Add New Customer Portal Authorized User'}
      </DialogTitle>
      
      <DialogContent>
        <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
          <div className="flex flex-col gap-16 pt-8">
            <TextField
              label={t('FIRST_NAME') || 'First Name'}
              variant="outlined"
              fullWidth
              value={formData.user_fname}
              onChange={(e) => handleFieldChange('user_fname', e.target.value)}
              disabled={loading}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: '#fff',
                  border: '1px solid #e2e1e5',
                  padding: '6px 9px',
                  height: '43px',
                },
              }}
            />

            <TextField
              label={t('LAST_NAME') || 'Last Name'}
              variant="outlined"
              fullWidth
              value={formData.user_lname}
              onChange={(e) => handleFieldChange('user_lname', e.target.value)}
              disabled={loading}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: '#fff',
                  border: '1px solid #e2e1e5',
                  padding: '6px 9px',
                  height: '43px',
                },
              }}
            />

            <TextField
              label={t('EMAIL') || 'Email'}
              variant="outlined"
              fullWidth
              type="email"
              value={formData.user_email}
              onChange={(e) => handleFieldChange('user_email', e.target.value)}
              disabled={loading}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: '#fff',
                  border: '1px solid #e2e1e5',
                  padding: '6px 9px',
                  height: '43px',
                },
              }}
            />

            <TextField
              label={t('PHONE') || 'Phone Number'}
              variant="outlined"
              fullWidth
              type="tel"
              value={formData.user_phone}
              onChange={(e) => {
                // Only allow numbers, spaces, dashes, parentheses, and plus
                const value = e.target.value.replace(/[^\d\s\-\(\)\+]/g, '');
                handleFieldChange('user_phone', value);
              }}
              disabled={loading}
              required
              placeholder="(555) 123-4567"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Select
                      value={formData.country_code}
                      onChange={(e) => handleFieldChange('country_code', e.target.value)}
                      disabled={loading}
                      variant="standard"
                      disableUnderline
                      sx={{
                        '& .MuiSelect-select': {
                          paddingRight: '24px !important',
                          fontSize: '16px',
                        }
                      }}
                    >
                      {COUNTRY_CODES.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.flag} {country.dial}
                        </MenuItem>
                      ))}
                    </Select>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: '#fff',
                  border: '1px solid #e2e1e5',
                  paddingLeft: '4px',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '10px 14px',
                },
              }}
            />

            <FormControl fullWidth required>
              <InputLabel id="portal-role-select-label">
                {t('USER_ROLE') || 'User Role'}
              </InputLabel>
              <Select
                labelId="portal-role-select-label"
                id="portal-role-select"
                value={formData.role_name}
                label={t('USER_ROLE') || 'User Role'}
                onChange={(e) => handleFieldChange('role_name', e.target.value)}
                disabled={loading}
                sx={{
                  background: '#fff',
                  border: '1px solid #e2e1e5',
                  height: '43px',
                  color: '#000',
                  '& .MuiSelect-select': {
                    color: '#000',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#fff',
                      '& .MuiMenuItem-root': {
                        color: '#000',
                        backgroundColor: '#fff',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#e0e0e0',
                          '&:hover': {
                            backgroundColor: '#d5d5d5',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                {PORTAL_ROLES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <div>
                      <div style={{ fontWeight: 500, color: '#000' }}>{role.label}</div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>
                        {role.description}
                      </div>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('CANCEL') || 'Cancel'}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="primary"
          disabled={loading}
          style={{ color: 'black' }}
        >
          {loading ? (t('SENDING_INVITATION') || 'Sending Invitation...') : (t('SEND_INVITATION') || 'Send Invitation')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

